import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { formatDateAU } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, Search, Building2, ChevronRight, CheckCircle2, XCircle, Plus, Pencil, Trash2, Upload
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AddEntityModal from '@/components/modals/AddEntityModal';
import { BulkEntitiesDialog } from '@/components/bulk/BulkRecordsDialogs';
import { toast } from 'sonner';

export default function ClientEntities() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [entities, setEntities] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchEntities = async () => {
    if (!token || !clientId) return;
    try {
      const response = await api.getEntities(token, clientId);
      setEntities(response.data);
    } catch (error) {
      console.error('Failed to fetch entities:', error);
    }
    setLoading(false);
  };

  const fetchIndividuals = async () => {
    if (!token || !clientId) return;
    try {
      const response = await api.getIndividuals(token, clientId);
      setIndividuals(response.data);
    } catch (error) {
      console.error('Failed to fetch individuals:', error);
    }
  };

  useEffect(() => {
    fetchEntities();
    fetchIndividuals();
  }, [token, clientId]);

  const handleAddEntity = async (data) => {
    try {
      await api.createEntity(token, clientId, data);
      toast.success('Entity added successfully');
      await fetchEntities();
    } catch (error) {
      console.error('Failed to add entity:', error);
      toast.error('Failed to add entity');
      throw error;
    }
  };

  const handleEditEntity = async (data) => {
    try {
      await api.updateEntity(token, clientId, editingEntity.id, data);
      toast.success('Entity updated successfully');
      await fetchEntities();
      setEditingEntity(null);
    } catch (error) {
      console.error('Failed to update entity:', error);
      toast.error('Failed to update entity');
      throw error;
    }
  };

  const handleDeleteEntity = async (entity) => {
    try {
      await api.deleteEntity(token, clientId, entity.id);
      toast.success('Entity deleted successfully');
      await fetchEntities();
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete entity:', error);
      toast.error('Failed to delete entity');
    }
  };

  const entityTypes = [...new Set(entities.map(e => e.type))];

  const filteredEntities = entities.filter(ent => {
    const matchesSearch = ent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          ent.abn?.includes(searchQuery);
    const matchesType = typeFilter === 'all' || ent.type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="entities-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/app/clients/${clientId}/overview`)}
            className="caffeine-btn-ghost mb-2"
            data-testid="back-to-overview-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to overview
          </Button>
          <h1 className="font-heading text-2xl text-[#2C2420]">Entities & Roles</h1>
          <p className="text-[#5D524D]">{filteredEntities.length} entities for {client?.name}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => setShowBulkModal(true)}
            variant="outline"
            data-testid="bulk-entities-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>
          <Button 
            onClick={() => setShowAddModal(true)}
            className="caffeine-btn"
            data-testid="add-entity-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Entity
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8078]" />
          <Input
            placeholder="Search by name or ABN..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="caffeine-input pl-10"
            data-testid="entities-search-input"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px] caffeine-input" data-testid="type-filter">
            <Building2 className="w-4 h-4 mr-2 text-[#8C8078]" />
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {entityTypes.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Entities Table */}
      {filteredEntities.length > 0 ? (
        <div className="caffeine-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Entity Name</th>
                <th>Type</th>
                <th>ABN</th>
                <th>ACN</th>
                <th>Est. Date</th>
                <th>Vesting Date</th>
                <th>Authority Set</th>
                <th>Linked People</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredEntities.map(ent => (
                <tr key={ent.id} data-testid={`entity-row-${ent.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-[#4A3B32]" />
                      <span className="font-medium text-[#2C2420]">{ent.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      ent.type === 'Company' ? 'bg-[#4A3B32]/10 text-[#4A3B32]' :
                      ent.type === 'Trust' ? 'bg-[#6B8E6B]/10 text-[#6B8E6B]' :
                      ent.type === 'SMSF' ? 'bg-[#D99A6C]/10 text-[#D99A6C]' :
                      ent.type === 'Foundation' ? 'bg-[#7D8C9E]/10 text-[#7D8C9E]' :
                      'bg-[#F0EBE6] text-[#5D524D]'
                    }`}>
                      {ent.type}
                    </span>
                  </td>
                  <td className="font-mono text-sm">{ent.abn || '—'}</td>
                  <td className="font-mono text-sm">{ent.acn || '—'}</td>
                  <td>{formatDateAU(ent.establishmentDate)}</td>
                  <td>{ent.vestingDate ? formatDateAU(ent.vestingDate) : '—'}</td>
                  <td>
                    {ent.authoritySet ? (
                      <CheckCircle2 className="w-5 h-5 text-[#6B8E6B]" />
                    ) : (
                      <XCircle className="w-5 h-5 text-[#C25B5B]" />
                    )}
                  </td>
                  <td className="tabular-nums">{ent.linkedPeople?.length || 0}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setEditingEntity(ent)}
                        className="caffeine-btn-ghost p-2"
                        data-testid={`edit-entity-${ent.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setDeleteConfirm(ent)}
                        className="caffeine-btn-ghost p-2 text-red-600 hover:text-red-700"
                        data-testid={`delete-entity-${ent.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/app/clients/${clientId}/entities/${ent.id}`)}
                        className="caffeine-btn-ghost"
                        data-testid={`view-entity-${ent.id}`}
                      >
                        View
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <Building2 className="w-12 h-12" />
          <p className="text-lg font-medium text-[#5D524D]">No entities found</p>
          <p className="text-sm">Try adjusting your search or filters</p>
        </div>
      )}

      {/* Add Entity Modal */}
      <AddEntityModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddEntity}
        clientId={clientId}
        individuals={individuals}
      />

      {/* Bulk Entities Import */}
      <BulkEntitiesDialog
        open={showBulkModal}
        onOpenChange={setShowBulkModal}
        clientId={clientId}
        onImported={fetchEntities}
      />

      {/* Edit Entity Modal */}
      <AddEntityModal
        isOpen={!!editingEntity}
        onClose={() => setEditingEntity(null)}
        onSave={handleEditEntity}
        clientId={clientId}
        initialData={editingEntity}
        isEdit={true}
        individuals={individuals}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entity</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteConfirm?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteEntity(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
